import { useEffect, useRef } from "react";
import { Link } from "react-router";
import { useCart } from "../cart/CartProvider";
import { maxQuantityFor } from "../cart/cartReducer";
import { useI18n } from "../i18n/I18nProvider";
import { useMoney } from "../lib/CurrencyProvider";
import { imageUrl } from "../lib/api";
import { variantLabel } from "../../shared/variants";
import { useComingSoon, useStoreSales } from "../lib/CatalogProvider";
import { QuantityStepper } from "./QuantityStepper";

export function CartDrawer() {
  const { t, pick, locale } = useI18n();
  const { format: formatPrice, formatLines } = useMoney();
  const { state, count, isOpen, close, setQuantity, remove } = useCart();
  const panelRef = useRef<HTMLDivElement>(null);
  const comingSoon = useComingSoon();
  const storeSales = useStoreSales();

  useEffect(() => {
    if (!isOpen) return;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    document.body.classList.add("no-scroll");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("no-scroll");
    };
  }, [isOpen, close]);

  if (!isOpen || comingSoon || !storeSales) return null;

  return (
    <div className="drawer-root">
      <div className="drawer-backdrop" onClick={close} aria-hidden="true" />
      <div className="drawer" role="dialog" aria-modal="true" aria-labelledby="cart-title" tabIndex={-1} ref={panelRef}>
        <div className="drawer-head">
          <h2 id="cart-title">
            {t("cart.title")} <small>({t("cart.items", { count })})</small>
          </h2>
          <button type="button" className="icon-btn" aria-label={t("nav.close")} onClick={close}>
            ✕
          </button>
        </div>

        {state.lines.length === 0 ? (
          <div className="drawer-empty">
            <p>{t("cart.empty")}</p>
            <Link to="/mercado" className="btn btn-primary" onClick={close}>
              {t("cart.continue")}
            </Link>
          </div>
        ) : (
          <>
            <ul className="cart-lines">
              {state.lines.map((line) => {
                const name = pick(line.name);
                return (
                  <li key={line.variantId} className="cart-line">
                    <img src={imageUrl(line.imageKey)} alt="" width={64} height={64} />
                    <div className="cart-line-info">
                      <Link to={`/producto/${line.slug}`} onClick={close}>
                        {name}
                      </Link>
                      <span className="cart-line-variant">
                        {variantLabel(line.variant, locale)}
                        {line.presale && <span className="tag tag-presale tag-inline">{t("product.presale")}</span>}
                      </span>
                      <span className="cart-line-price">{formatPrice(line.price, line.priceArs)}</span>
                      <div className="cart-line-actions">
                        <QuantityStepper
                          label={`${t("product.quantity")}: ${name}`}
                          value={line.quantity}
                          max={maxQuantityFor(line.stock)}
                          onChange={(q) => setQuantity(line.variantId, q)}
                        />
                        <button type="button" className="btn btn-link" onClick={() => remove(line.variantId)}>
                          {t("cart.remove")}
                        </button>
                      </div>
                    </div>
                    <strong className="cart-line-total">{formatLines([{ unitPrice: line.price, unitPriceArs: line.priceArs, quantity: line.quantity }])}</strong>
                  </li>
                );
              })}
            </ul>
            {state.lines.some((l) => l.presale) && <p className="presale-note cart-presale-note">{t("cart.presaleNote")}</p>}
            <div className="drawer-foot">
              <div className="subtotal">
                <span>{t("cart.subtotal")}</span>
                <strong data-testid="cart-subtotal">{formatLines(state.lines.map((l) => ({ unitPrice: l.price, unitPriceArs: l.priceArs, quantity: l.quantity })))}</strong>
              </div>
              <Link to="/checkout" className="btn btn-primary btn-block" onClick={close}>
                {t("cart.checkout")}
              </Link>
              <button type="button" className="btn btn-link btn-block" onClick={close}>
                {t("cart.continue")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
